import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Leaf, Heart, ArrowRight } from "lucide-react";

const LAKE = "https://images.unsplash.com/photo-1699116548118-1605ae766335";

export function QuotePanel({ variant = "card" }) {
  const { t, lang } = useI18n();
  const [fav, setFav] = useState(null);
  const [daily, setDaily] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/favorites").then(({ data }) => {
      if (!active) return;
      const list = data.favorites || [];
      if (list.length) {
        const day = new Date().toISOString().slice(0, 10);
        let seed = 0;
        for (const ch of day) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
        setFav(list[seed % list.length]);
      } else {
        api.get(`/daily-quote?lang=${lang}`).then(({ data }) => { if (active) setDaily(data?.quote || ""); }).catch(() => {});
      }
    }).catch(() => {});
    return () => { active = false; };
  }, [lang]);

  const text = fav?.text || daily;
  if (!text) return null;
  const fromFav = !!fav;

  const saveDaily = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/favorites", { text, source: "ai" });
      toast.success(t("quote.saved"));
      setFav({ text, source: "ai" });
    } catch {
      toast.error("Couldn't save that just now.");
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="ambient-shadow rounded-3xl border border-border/60 bg-gradient-to-b from-accent/40 to-card p-6" data-testid="quote-panel">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Leaf className="h-3.5 w-3.5" strokeWidth={1.5} />
        </span>
        {fromFav ? t("favorites.fromFavorites") : t("quote.title")}
      </div>
      <p className="mt-4 font-serif text-xl italic leading-snug" data-testid="quote-panel-text">“{text}”</p>
      <div className="mt-5 flex items-center gap-2">
        {!fromFav && (
          <button onClick={saveDaily} disabled={saving} data-testid="quote-panel-save"
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-50">
            <Heart className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("quote.saveShort")}
          </button>
        )}
        <Link to="/app/favorites" data-testid="quote-panel-viewall"
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
          {t("favorites.viewAll")} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
        </Link>
      </div>
    </motion.div>
  );

  if (variant === "rail") {
    return (
      <div className="sticky top-6 space-y-4" data-testid="quote-rail">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="ambient-shadow relative overflow-hidden rounded-3xl border border-border/60">
          <img src={LAKE} alt="" className="h-44 w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
        </motion.div>
        {body}
      </div>
    );
  }
  return body;
}
